import type {ImageCatalog} from 'microsites/core/chatMicrosite/images/imageTypes';

// NOTE: Do not import ornament-divider.svg (or any SVG) here—vector URLs come from Gatsby public assets.

const imageCatalog = {
  aceAndProudAlt: {
    id: `aceAndProudAlt`,
    kind: `raster`,
    width: 1024,
    height: 1024,
    description: `Realistic selfie of a Middle Eastern woman wearing a mustard sweater with an ace pride flag pin, softly lit against a teal wall for a natural, warm look.`,
    prompt: `Realistic 1:1 selfie of a Middle Eastern woman in a completely different setting and outfit. Casual natural lighting, different clothes and background--perhaps outdoors at sunset or in a cozy modern room. Authentic expression, realistic and candid, warm tones but distinct from previous cafe images.`,
  },
  aceAndProudSelfie: {
    id: `aceAndProudSelfie`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Casual selfie of the same asexual woman from the cafe scene, taken at a slight upward angle with imperfect indoor lighting and natural skin texture for an authentic, unposed feel.`,
    prompt: `Casual, unposed selfie of the same woman from the cafe, captured at a slight angle with imperfect indoor lighting and natural skin texture. Looks like a phone selfie--slightly blurry, realistic, authentic, and unedited.`,
  },
  hero1: {
    id: `hero1`,
    kind: `raster`,
    width: 1536,
    height: 1024,
    description: `Cinematic wide shot of a Brooklyn rooftop party celebrating "alias launch" night with neon banners and encrypted HUD graphics.`,
    prompt: `Photorealistic 16:9 cinematic shot of a diverse group on a Brooklyn rooftop under glowing alias banners. Include holographic UI overlays that read "Free Anonymous Chat" and "Encrypted Suite Live", magenta/teal lighting, moody clouds, and stylish streetwear.`,
  },
  hero2: {
    id: `hero2`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Palm Springs micro-suite corridor where NFC bracelets unlock private sexting rooms.`,
    prompt: `Photorealistic 9:16 portrait inside an upscale Palm Springs corridor. Two travelers tap NFC bracelets on a console labeled "Secure Suite Ready"; include succulents, terrazzo, mid-century accents, and warm desert light.`,
  },
  neonBackchannelCrew: {
    id: `neonBackchannelCrew`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Nightlife regulars trading QR passes under rain-slick neon signage.`,
    prompt: `Photorealistic 3:4 urban night photo of queer friends leaning against a graffiti wall in the rain. One person holds a glowing QR card with a lock icon, neon reflections hit wet pavement, expressions are playful and conspiratorial.`,
  },
  audioIntroStudio: {
    id: `audioIntroStudio`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Minimalist chill-out room with audio intro kiosks and soft seating for nervous newcomers.`,
    prompt: `Photorealistic 9:16 interior portrait with circular seating, calming plants, and "audio intro" headphone kiosks. Feature varied body types relaxing under warm amber lighting, no beverages or illustrations.`,
  },
  adaptiveFieldDay: {
    id: `adaptiveFieldDay`,
    kind: `raster`,
    width: 1536,
    height: 1024,
    description: `Outdoor picnic where mobility aids are decorated with LED stickers celebrating secure chat wins.`,
    prompt: `Photorealistic 3:2 landscape of an accessible picnic. Show wheelchair users, cane users, limb differences, and LED stickers reading "proxy shield" or "friend finder" on mobility aids. Soft afternoon sun, candid laughter.`,
  },
  affinity1: {
    id: `affinity1`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Cinematic portrait of two tall men on a sunny beach wearing colorful retro 1980s swimwear and smiling in nostalgic light.`,
    prompt: `Portrait-oriented cinematic photo of two tall men on a sunny beach, wearing stylish 1980s-inspired swimwear with colorful retro shorts and light summer shirts open in the breeze. They stand close together, smiling in a relaxed, authentic pose while soft ocean waves, bright sand, and warm sunlight with subtle nostalgic film grain fill the background. Palette leans into turquoise, coral, and golden tones with shallow depth of field and professional lifestyle lighting.`,
  },
  affinity2: {
    id: `affinity2`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Playful editorial portrait of friends in bunny-ear headbands and retro swimsuits laughing in a pastel photo studio.`,
    prompt: `Colorful, high-energy fashion photo of a group of friends posing in a playful themed costume shoot. They wear fun bunny-ear headbands and bright retro swimsuits, laughing and playing with pillows on a soft pastel set that feels like a cozy photo studio. The mood stays joyful, mischievous, and humorous with no sexualization. Light everything with bright cinematic pop-art vibes for professional editorial photography.`,
  },
  sapphicSignalStudio: {
    id: `sapphicSignalStudio`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Sapphic living room with projector showing anonymous DMs and a playlist vote.`,
    prompt: `Photorealistic 3:4 indoor scene of two sapphic partners on a couch watching a projector that shows flirty anonymous DMs and a music poll. Bold art-deco lighting, velvet sofa, cozy textiles, no drinks.`,
  },
  polyCalendarLab: {
    id: `polyCalendarLab`,
    kind: `raster`,
    width: 1024,
    height: 1024,
    description: `Studio workspace with tablets showing secure calendars for polycule scheduling.`,
    prompt: `Photorealistic 4:3 landscape of a modern studio with glass table, styluses, and tablets showing color-coded calendars titled "Secure Suite" and "Brunch Flirt". Include plus-size queer folks chatting.`,
  },
  galaxyTagBooth: {
    id: `galaxyTagBooth`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `LED-filled booth where nonbinary chatters customize neon pronoun tags.`,
    prompt: `Photorealistic 9:16 portrait of a booth drenched in purple/blue LEDs where a nonbinary person in cyberpunk fashion arranges glowing pronoun tags on a magnetic wall, with floating lock/star icon projections.`,
  },
  ropeOpsDeck: {
    id: `ropeOpsDeck`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Operations platform in a Detroit warehouse that tracks rope scenes, hydration, and secure audio feeds.`,
    prompt: `Photorealistic 3:4 documentary shot of riggers prepping ropes near a station displaying hydration timers and audio-link statuses. Industrial windows, warm spotlights, confident expressions.`,
  },
  proxyYachtDeck: {
    id: `proxyYachtDeck`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Luxury yacht deck at twilight where alias wristbands unlock afterparty invites.`,
    prompt: `Photorealistic 9:16 portrait of a glamorous queer couple scanning glowing wristbands on a yacht console at dusk, Miami skyline behind them, holographic arrows pointing to geofenced parties.`,
  },
  ridgeSignalStation: {
    id: `ridgeSignalStation`,
    kind: `raster`,
    width: 1024,
    height: 1024,
    description: `High-altitude meetup with satellite repeaters and neon "anonymous chat online" sign.`,
    prompt: `Photorealistic 4:3 landscape of queer outdoorsy friends around a portable satellite repeater. Include an LED sign reading "Anon Chat Online", snow-capped peaks, sunrise colors, and layered gear.`,
  },
  sunsetFriendFinder: {
    id: `sunsetFriendFinder`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Pasture circle at golden hour with holographic timezone schedulers in the sky.`,
    prompt: `Photorealistic 9:16 portrait of a queer group lounging on blankets during golden hour with a translucent AR schedule overhead showing time zones and "friend match". Soft lens flares, nostalgic vibes.`,
  },
  howItWorks1: {
    id: `howItWorks1`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `High-energy portrait of friends in colorful retro outfits with bunny-ear headbands mid-jump on a confetti-packed neon studio set.`,
    prompt: `Vibrant, high-energy fashion photo shoot featuring a group of friends in colorful retro outfits and playful bunny-ear headbands. They are on a bright set filled with balloons, confetti, and pillows, laughing, jumping, and striking exaggerated fun poses. The scene has an over-the-top 1980s pop-art vibe - bold neon lighting, cinematic motion blur, and lots of expression and humor. The mood is chaotic, fashionable, and cheeky, like a parody of vintage party culture. Professional editorial photography style, sharp focus on color, movement, and joy.`,
  },
  howItWorks2: {
    id: `howItWorks2`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Editorial portrait of retro party friends with bunny-ear headbands tossing pillows through neon confetti and balloons.`,
    prompt: `Vibrant, high-energy fashion photo shoot featuring a group of friends in colorful retro outfits and playful bunny-ear headbands. They are on a bright set filled with balloons, confetti, and pillows, laughing, jumping, and striking exaggerated fun poses. The scene has an over-the-top 1980s pop-art vibe - bold neon lighting, cinematic motion blur, and lots of expression and humor. The mood is chaotic, fashionable, and cheeky, like a parody of vintage party culture. Professional editorial photography style, sharp focus on color, movement, and joy.`,
  },
  insights1: {
    id: `insights1`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Close portrait of a communications operator in a mobile command module gripping a satcom handset beside an encrypted tablet, bathed in green status light reflections.`,
    prompt: `Tight, high-detail portrait of a communications operator in a mobile command module, hands on a hardened satcom handset and encrypted comms tablet. Reflections of green status indicators light the operator’s face. Camera: 85mm, shallow depth of field, crisp textures, realistic skin tones. Ambient red/blue LED glow and soft window backlight create cinematic contrast. Professional editorial finish, no identifying insignia or text.`,
  },
  insights2: {
    id: `insights2`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Landscape-friendly view inside a field command center where a diverse team studies a holographic satellite map under low tactical lighting and encrypted UI projections.`,
    prompt: `Interior of a modern field command center with a multi-person team around a holographic map projection showing satellite nodes. Ambient low light, tactical maps, and encrypted-style UI elements (abstract bars and nodes -- no real schematics). Diverse team collaborating, intense focus, cinematic documentary look with shallow depth and crisp detail. Landscape, hero banner friendly.`,
  },
  anonymousChat1: {
    id: `anonymousChat1`,
    kind: `raster`,
    width: 1024,
    height: 1365,
    description: `Artistic close-up of a futuristic encryption microchip with glowing cyan circuitry and engraved icons—a lock, Wi-Fi signal, shield, and waveform—etched into its metallic surface. The board around it features intricate glowing traces and microcomponents under cinematic cyan and magenta light, emphasizing security and advanced hardware design.`,
    prompt: `Ultra-realistic 3:4 artistic close-up portrait of a futuristic encryption microchip with intricate glowing circuitry and detailed metallic textures. The chip surface features abstract engraved symbols—lock, Wi-Fi signal, shield, waveform—no text. The design looks advanced, elegant, and secure, surrounded by microcomponents and illuminated with a mix of cyan and magenta reflections for a cinematic high-tech atmosphere.`,
  },
  anonymousChat2: {
    id: `anonymousChat2`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Candid portrait of an attractive male-female couple flirting on plush pillows while their alias bands glow with lock, spark, and heart icons and fiber-optic trails link their phone to a proxy router on the table.`,
    prompt: `Photorealistic 3:4 portrait of an attractive male-female couple flirting in a dim apartment lounge. She lounges against rust-colored pillows while he leans in, both smiling at a phone. Their wrists wear matte alias bands emitting tiny lock, spark, and heart icons. Fiber-optic streaks arc from the phone to a proxy router cube on the low table, dotted with shield glyphs to imply encrypted messaging. Mix warm magenta key light with cobalt rim light; keep clothing slightly tousled, skin texture real, and omit all text or logos.`,
  },
  valueProps1: {
    id: `valueProps1`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Isometric encryption scene with a digital photo sliced into floating tiles, a glowing slice mid-decryption, and dimmed decoy slices marked by subtle overlays.`,
    prompt: `Photorealistic 3:4 isometric render showing a digital image split into floating rectangular slices. Foreground slices snap together with neon seams, mid-flight slices streak in on orange motion trails, and a central slice glows green with a tiny unlocking icon plus cascading binary (0/1). Off to the right, dimmed decoy slices hover, clearly fake via muted colors and offset alignment. Add translucent padlock icons, thin neon connectors, a small 60% progress bar above the decrypting slice, and miniature labels “assembled”, “incoming”, “decoy”, “decrypting”. Deep navy background, cyan/neon green highlights, glossy slice surfaces, soft studio lighting, portrait ratio, no real text—just UI glyphs.`,
  },
  valueProps2: {
    id: `valueProps2`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Groovy retro poster with the quote “What I say must stay only between us” in bold 70s typography, cream backdrop, and swirling orange doodles.`,
    prompt: `Photorealistic 3:4 groovy retro poster: cream background, bold 1970s typography in deep red and warm pink spelling “What I say must stay only between us”. Surround the quote with thick orange doodles, playful drop shadows, and dopamine-decor flourishes. Keep the layout funky yet clean, include soft grain, and ensure high-resolution portrait ratio.`,
  },
  socialProof1: {
    id: `socialProof1`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `High-end privacy booth with two elegant women lounging on a velvet chaise, laughing together while an encrypted chat glows on a glass tablet and translucent lock + heart icons arc overhead.`,
    prompt: `Photorealistic 3:4 portrait of a happy lesbian couple inside an ultra-luxury privacy suite. Velvet chaise, smoked-glass walls, brass sconces, and a chilled sparkling water tray set the mood. One woman in a satin jumpsuit holds a glass tablet with softly blurred chat bubbles; the other, in a fitted blazer dress, leans close, smiling. Subtle translucent lock and heart glyphs hover over the tablet to show end-to-end encryption. Add diffuser lighting, plush textiles, and zero readable text.`,
  },
  socialProof2: {
    id: `socialProof2`,
    kind: `raster`,
    width: 1024,
    height: 1536,
    description: `Exclusive hotel privacy lounge featuring three stylish friends sharing phones over a marble console while neon lock and shield icons reflect off mirrored panels to emphasize secure messaging.`,
    prompt: `Photorealistic 3:4 portrait of three friends inside an exclusive hotel privacy lounge. Marble console table, floor-to-ceiling acoustic curtains, and soft gold uplighting. Each friend holds a phone emitting faint lock, shield, and spark icons to suggest encrypted chat. Include mirrored wall panels, artisan mocktails, and a discrete concierge call light. Keep expressions relaxed and happy, show designer outfits, and ensure there is no legible UI text—only glowing encryption glyphs.`,
  },
  ornamentDivider: {
    id: `ornamentDivider`,
    kind: `vector`,
    description: `Linear ornament divider used to separate major landing page sections.`,
    prompt: null,
  },
  author01: {
    id: `author01`,
    kind: `raster`,
    width: 720,
    height: 1080,
    description: `In-house portrait of Selene Quinn shot against a charcoal studio backdrop with soft pink rim lighting for editorial bios.`,
    prompt: null,
  },
} as const;

export const images = imageCatalog satisfies ImageCatalog<typeof imageCatalog>;

export type ImageKey = keyof typeof images;
